import { useAuth } from "@/auth/hooks/useAuth";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { ChevronDown, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AddDocuments from "./AddDocuments";
import AddDocumentFolderButton from "./AddDocumentFolderButton";
import ImportDocumentFolderButton from "./ImportDocumentFolderButton";
import DateFilter from "./DateFilter";
import DropdownFilterCat from "./DropdownFilterCat";
import DropdownFilterType from "./DropdownFilterType";

type DocumentItem = {
  id: number;
  name: string;
  path: string;
  pp: number;
  program: number;
  project: number;
  type: number;
  origin?: string;
  subcontractorId?: number | null;
  intervenants?: Array<{
    id: number;
  }>;
  at?: {
    date?: string;
  };
};

type DocumentFolderNode = {
  id?: number | null;
  defaultKey?: string | null;
  documentType?: number | null;
  documentTypes?: number[];
  children?: DocumentFolderNode[];
};

type DocumentFolderTreeResponse = {
  porteur?: Record<string, DocumentFolderNode[]>;
  project?: Record<string, DocumentFolderNode[]>;
  intervenant?: Record<string, DocumentFolderNode[]>;
};

const FilterDocument = ({
  typesDocs,
  optionsType,
  dataDocuments,
  setDataFiltered,
  programsList,
  projectList,
  hideFilters = false,
  hideProjectProgramCategoryFilters = false,
  requireChildFolderForAddFolder = false,
  showAddFolderButton = true,
  ownerId = null,
  customFolders,
  searchParamsOverride,
  lockedSearchParams,
}: {
  typesDocs: any;
  optionsType: any;
  dataDocuments: any;
  setDataFiltered: any;
  programsList?: any[];
  projectList?: any[];
  hideFilters?: boolean;
  hideProjectProgramCategoryFilters?: boolean;
  requireChildFolderForAddFolder?: boolean;
  showAddFolderButton?: boolean;
  ownerId?: number | null;
  customFolders?: DocumentFolderTreeResponse;
  searchParamsOverride?: URLSearchParams;
  lockedSearchParams?: Record<string, string | number | null | undefined>;
}) => {
  const navigate = useNavigate();
  const { idPp } = useParams();
  const [searchParams] = useSearchParams();
  const searchParamsKey = (searchParamsOverride ?? searchParams).toString();
  const EMPTY_FILTER_SENTINEL = "__empty__";
  const filterParamKeys = [
    "search",
    "categories",
    "types",
    "programs",
    "projects",
    "folderIds",
    "intervenants",
    "subcontractors",
    "origin",
    "date",
    "after",
    "before",
  ];

  const defaultFilter = useMemo(
    () => ({
      search: "",
      categories: typesDocs.flatMap((o: any) => [
        o.value,
        ...(o.children?.map((c: any) => c.value) || []),
      ]) as number[],
      types: optionsType.map((o: any) => o.value) as string[],
      programs: [] as number[],
      projects: [] as number[],
      folderIds: [] as number[],
      intervenants: [] as number[],
      origin: null as string | null,
      date: null as string | null,
      after: null as string | null,
      before: null as string | null,
    }),
    [optionsType, typesDocs],
  );

  const [filter, setFilter] = useState(defaultFilter);

  const { t } = useTranslation("dashboard", {
    keyPrefix: "details",
  });

  const [searchValue, setSearchValue] = useState(filter.search);
  const { user } = useAuth();
  const hidePostActions = useHidePostActions();
  const [hasAccessToEdit, setHasAccessToEdit] = useState(true);
  const collectFolderIds = (folders: DocumentFolderNode[] = []): number[] =>
    folders.flatMap((folder) => {
      const folderId = Number(folder?.id ?? 0);
      return [
        ...(Number.isFinite(folderId) && folderId > 0 ? [folderId] : []),
        ...collectFolderIds(folder.children ?? []),
      ];
    });

  const findFolderNode = (
    folders: DocumentFolderNode[] = [],
    folderId: number,
  ): DocumentFolderNode | null => {
    for (const folder of folders) {
      if (Number(folder?.id ?? 0) === folderId) {
        return folder;
      }

      const nested = findFolderNode(folder.children ?? [], folderId);
      if (nested) {
        return nested;
      }
    }

    return null;
  };

  const findFolderPath = (
    folders: DocumentFolderNode[] = [],
    folderId: number,
  ): DocumentFolderNode[] | null => {
    for (const folder of folders) {
      if (Number(folder?.id ?? 0) === folderId) {
        return [folder];
      }

      const nested = findFolderPath(folder.children ?? [], folderId);
      if (nested) {
        return [folder, ...nested];
      }
    }

    return null;
  };

  const getFolderTypeIds = (folder: DocumentFolderNode): number[] => {
    const defaultKey = String(folder.defaultKey ?? "");

    if (defaultKey.startsWith("type-")) {
      const typeId = Number(defaultKey.replace("type-", ""));
      return Number.isFinite(typeId) && typeId > 0 ? [typeId] : [];
    }

    if (defaultKey === "contractuel") {
      return [25, 26, 27];
    }

    if (defaultKey === "rapport") {
      return [10, 11, 12, 13, 14, 15];
    }

    const documentTypes = Array.isArray(folder.documentTypes)
      ? folder.documentTypes.map(Number).filter((value) => Number.isFinite(value) && value > 0)
      : [];

    if (documentTypes.length > 0) {
      return documentTypes;
    }

    const documentType = Number(folder.documentType ?? 0);
    return Number.isFinite(documentType) && documentType > 0 ? [documentType] : [];
  };

  const folderFilterIds = useMemo(() => {
    if (filter.folderIds.length === 0) {
      return [];
    }

    const scopeFolders = [
      ...Object.values(customFolders?.porteur ?? {}).flat(),
      ...Object.values(customFolders?.project ?? {}).flat(),
      ...Object.values(customFolders?.intervenant ?? {}).flat(),
    ];

    return [
      ...new Set(
        filter.folderIds.flatMap((folderId) => {
          const folder = findFolderNode(scopeFolders, folderId);
          return folder ? collectFolderIds([folder]) : [folderId];
        }),
      ),
    ];
  }, [customFolders, filter.folderIds]);
  const isDocumentsRootView =
    !(searchParams.get("scope") ?? "") &&
    !(searchParams.get("programs") ?? "") &&
    !(searchParams.get("projects") ?? "") &&
    !(searchParams.get("folderIds") ?? "") &&
    !(searchParams.get("intervenants") ?? "") &&
    !(searchParams.get("origin") ?? "");

  useEffect(() => {
    if (user) {
      setHasAccessToEdit(
        user?.roles?.includes("ROLE_CARRIER") ||
        user?.roles?.includes("ROLE_CPINTERVENANT") ||
        (user?.roles?.includes("ROLE_SUBCONTRACTOR") && user?.fullAccess),
      );
    }
  }, [user]);

  const allCategoryValues = useMemo(
    () =>
      typesDocs.flatMap((item: any) => [
        Number(item.value),
        ...(item.children?.map((child: any) => Number(child.value)) || []),
      ]),
    [typesDocs],
  );

  const selectedCategoryLabels = useMemo(() => {
    const labels: string[] = [];

    typesDocs.forEach((parent: any) => {
      if (filter.categories.includes(Number(parent.value))) {
        labels.push(parent.label);
        return;
      }

      parent.children?.forEach((child: any) => {
        if (filter.categories.includes(Number(child.value))) {
          labels.push(child.label);
        }
      });
    });

    return labels;
  }, [filter.categories, typesDocs]);

  const showSelectedCategories =
    filter.categories.length > 0 &&
    filter.categories.length < allCategoryValues.length;


  // 🔹 IDs de projets autorisés selon les programmes sélectionnés
  const validProjectIds = useMemo(() => {
    if (!filter.programs.length || !programsList?.length) return [];

    return programsList
      .filter((program: any) => filter.programs.includes(program.id))
      .flatMap((program: any) => program.projects || []);
  }, [filter.programs, programsList]);

  const projectProgramById = useMemo(() => {
    const mapping = new Map<number, number>();

    (programsList ?? []).forEach((program: any) => {
      const normalizedProgramId = Number(program?.id);
      if (!Number.isFinite(normalizedProgramId) || normalizedProgramId <= 0) {
        return;
      }

      (program?.projects ?? []).forEach((project: any) => {
        const normalizedProjectId = Number(
          typeof project === "object" && project !== null ? project.id : project,
        );
        if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) {
          return;
        }

        mapping.set(normalizedProjectId, normalizedProgramId);
      });
    });

    return mapping;
  }, [programsList]);

  // 🔹 Mise à jour filtre + URL
  const handleChange = (slug: string, value: any[]) => {
    setFilter((prev: any) => {
      const updatedFilter: any = { ...prev };

      if (["categories", "types", "programs", "projects", "folderIds", "intervenants"].includes(slug)) {
        updatedFilter[slug] = value;
      } else {
        updatedFilter[slug] = value.length ? value[0] : null;
      }

      const params = new URLSearchParams(searchParams.toString());

      filterParamKeys.forEach((key) => {
        params.delete(key);
      });

      Object.entries(updatedFilter).forEach(([key, val]: any) => {
        if (Array.isArray(val) && val.length === 0) {
          if (key === "categories" || key === "types") {
            params.set(key, EMPTY_FILTER_SENTINEL);
          }
          return;
        }

        if (
          val !== null &&
          val !== undefined &&
          val !== "" &&
          !(Array.isArray(val) && val.length === 0)
        ) {
          if (Array.isArray(val)) {
            params.set(key, val.join(","));
          } else {
            params.set(key, String(val));
          }
        }
      });

      Object.entries(lockedSearchParams ?? {}).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") {
          params.delete(key);
          return;
        }

        params.set(key, String(value));
      });

      navigate({ search: params.toString() });
      return updatedFilter;
    });
  };

  // 🔹 URL → State
  useEffect(() => {
    const queryParams = new URLSearchParams(searchParamsKey);
    const updatedFilter: any = {
      ...defaultFilter,
      categories: [...defaultFilter.categories],
      types: [...defaultFilter.types],
      programs: [...defaultFilter.programs],
      projects: [...defaultFilter.projects],
      folderIds: [...defaultFilter.folderIds],
      intervenants: [...defaultFilter.intervenants],
    };

    queryParams.forEach((value, key) => {
      if (key === "categories") {
        updatedFilter[key] = value === EMPTY_FILTER_SENTINEL ? [] : value.split(",").map(Number);
      } else if (key === "types") {
        updatedFilter[key] = value === EMPTY_FILTER_SENTINEL ? [] : value.split(",");
      } else if (key === "programs") {
        updatedFilter[key] = value.split(",").map(Number);
      } else if (key === "projects") {
        updatedFilter[key] = value.split(",").map(Number);
      } else if (key === "folderIds") {
        updatedFilter[key] = value.split(",").map(Number);
      } else if (key === "intervenants" || key === "subcontractors") {
        updatedFilter.intervenants = value.split(",").map(Number);
      } else if (key === "search") {
        updatedFilter[key] = value;
      } else if (key === "origin") {
        updatedFilter[key] = value;
      } else if (["after", "before", "date"].includes(key)) {
        updatedFilter[key] = value === "null" ? null : value;
      }
    });

    setFilter((prev: any) =>
      JSON.stringify(prev) === JSON.stringify(updatedFilter) ? prev : updatedFilter,
    );
    setSearchValue((prev) => (prev === updatedFilter.search ? prev : updatedFilter.search));
  }, [defaultFilter, searchParamsKey]);

  // 🔹 Nettoyer les projets sélectionnés invalides
  useEffect(() => {
    if (!filter.projects.length || !filter.programs.length) return;

    const filteredSelectedProjects = filter.projects.filter((id) =>
      validProjectIds.includes(id),
    );

    if (filteredSelectedProjects.length !== filter.projects.length) {
      handleChange("projects", filteredSelectedProjects);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validProjectIds]);

  const extensionGroups: Record<string, string[]> = {
    document: ["doc", "docx", "txt", "odt", "rtf"],
    spreadsheet: ["xls", "xlsx", "ods", "csv"],
    image: ["jpg", "jpeg", "png", "gif", "bmp", "webp"],
    pdf: ["pdf"],
  };

  // 🔹 Filtrage docs
  useEffect(() => {
    if (!dataDocuments) return;

    const allDocuments: DocumentItem[] = Object.values(dataDocuments).flat();
    const datasFiltered = allDocuments.filter((pj) => {
      const documentProgramId = Number(pj.program);
      const documentProjectId = Number(pj.project);
      const fileName = pj.name?.toLowerCase() || "";
      const extension = fileName.split(".").pop() || "";
      const isBlobImage = fileName === "blob";

      let groupFound = "";
      if (isBlobImage) {
        groupFound = "image";
      } else {
        for (const [group, extensions] of Object.entries(extensionGroups)) {
          if (extensions.includes(extension)) {
            groupFound = group;
            break;
          }
        }
      }

      const matchesSearch =
        !filter.search ||
        pj.name.toLowerCase().includes(filter.search.toLowerCase());

      const matchesTypes =
        !filter.types.length || filter.types.includes(groupFound);

      const matchesIdPp = !idPp || pj.pp === Number(idPp);

      const linkedFolderIds = Array.isArray((pj as any).documentFolderIds)
        ? (pj as any).documentFolderIds.map((value: any) => Number(value)).filter((value: number) => Number.isFinite(value))
        : [];
      const linkedDocumentFolderIds = Array.isArray((pj as any).documentFolders)
        ? (pj as any).documentFolders
            .map((folder: any) => Number(folder?.id))
            .filter((value: number) => Number.isFinite(value) && value > 0)
        : [];
      const linkedIntervenantFolderIds = Array.isArray((pj as any).documentFolders)
        ? (pj as any).documentFolders
            .filter((folder: any) => folder?.scopeType === "intervenant")
            .map((folder: any) => Number(folder?.scopeId))
            .filter((value: number) => Number.isFinite(value) && value > 0)
        : [];
      const linkedPorteurDefaultKeys = Array.isArray((pj as any).documentFolders)
        ? (pj as any).documentFolders
            .filter((folder: any) => folder?.scopeType === "porteur")
            .map((folder: any) => String(folder?.defaultKey ?? "").trim())
            .filter((value: string) => value.length > 0)
        : [];
      const linkedProjectScopeIds = Array.isArray((pj as any).documentFolders)
        ? (pj as any).documentFolders
            .filter((folder: any) => folder?.scopeType === "project")
            .map((folder: any) => Number(folder?.scopeId))
            .filter((value: number) => Number.isFinite(value) && value > 0)
        : [];
      const linkedAllFolderIds = [...new Set([...linkedFolderIds, ...linkedDocumentFolderIds])];
      const linkedFolderMatchesCategory =
        filter.categories.length > 0 &&
        linkedAllFolderIds.some((folderId) => {
          const scopeFolders = [
            ...Object.values(customFolders?.porteur ?? {}).flat(),
            ...Object.values(customFolders?.project ?? {}).flat(),
            ...Object.values(customFolders?.intervenant ?? {}).flat(),
          ];
          const folderPath = findFolderPath(scopeFolders, folderId) ?? [];

          return folderPath.some((folder) =>
            getFolderTypeIds(folder).some((typeId) => filter.categories.includes(typeId)),
          );
        });
      const effectiveProjectIds = Array.from(
        new Set(
          [documentProjectId, ...linkedProjectScopeIds].filter(
            (value) => Number.isFinite(value) && value > 0,
          ),
        ),
      );
      const effectiveProgramIds = Array.from(
        new Set(
          [
            documentProgramId,
            ...effectiveProjectIds
              .map((projectId) => projectProgramById.get(projectId) ?? null)
              .filter((value): value is number => Number.isFinite(value) && value > 0),
          ].filter((value) => Number.isFinite(value) && value > 0),
        ),
      );
      const isFolderOnlyDocument =
        linkedFolderIds.length > 0 &&
        (!documentProjectId || documentProjectId <= 0);

      const matchesProgram =
        isFolderOnlyDocument
          ? (
              filter.folderIds.length > 0 ||
              !filter.programs.length ||
              (
                filter.projects.length > 0 &&
                filter.projects.some((projectId) => effectiveProjectIds.includes(projectId))
              ) ||
              filter.programs.some(
                (programId) =>
                  effectiveProgramIds.includes(programId) ||
                  linkedPorteurDefaultKeys.includes(`program-${programId}`),
              )
            )
          : !filter.programs.length ||
            filter.programs.some((programId) => effectiveProgramIds.includes(programId));

      const matchesProject =
        isFolderOnlyDocument && filter.folderIds.length > 0
          ? true
          : !filter.projects.length ||
            filter.projects.some((projectId) => effectiveProjectIds.includes(projectId));

      const matchesFolder =
        !folderFilterIds.length ||
        folderFilterIds.some(
          (folderId) =>
            linkedFolderIds.includes(folderId) ||
            linkedDocumentFolderIds.includes(folderId),
        );

      const matchesFolderOnlyVisibility = true;

      const documentIntervenantIds = Array.isArray(pj.intervenants)
        ? pj.intervenants
            .map((intervenant) => Number(intervenant?.id))
            .filter((id) => Number.isFinite(id) && id > 0)
        : [];

      const matchesIntervenant =
        !filter.intervenants.length ||
        filter.intervenants.some((id) =>
          documentIntervenantIds.includes(id) || linkedIntervenantFolderIds.includes(id),
        );

      const matchesOrigin =
        filter.folderIds.length > 0
          || linkedIntervenantFolderIds.some((id) => filter.intervenants.includes(id))
          ? true
          : !filter.origin || pj.origin === filter.origin;

      const matchesDocType =
        !filter.categories.length ||
        filter.categories.includes(Number(pj.type)) ||
        linkedFolderMatchesCategory;

      let matchesDate = true;
      const docDate = pj.at?.date ? new Date(pj.at.date) : null;

      if (filter.after || filter.before) {
        if (docDate) {
          if (filter.after) {
            matchesDate = matchesDate && docDate >= new Date(filter.after);
          }
          if (filter.before) {
            matchesDate = matchesDate && docDate <= new Date(filter.before);
          }
        } else {
          matchesDate = false;
        }
      }

      if (filter.date && docDate) {
        const now = new Date();

        switch (filter.date) {
          case "today":
            matchesDate = docDate.toDateString() === now.toDateString();
            break;

          case "lastWeek": {
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(now.getDate() - 7);
            matchesDate = docDate >= sevenDaysAgo && docDate <= now;
            break;
          }

          case "lastMonth": {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(now.getDate() - 30);
            matchesDate = docDate >= thirtyDaysAgo && docDate <= now;
            break;
          }

          case "thisYear":
            matchesDate = docDate.getFullYear() === now.getFullYear();
            break;

          case "lastYear":
            matchesDate = docDate.getFullYear() === now.getFullYear() - 1;
            break;

          default:
            break;
        }
      }

      return (
        matchesSearch &&
        matchesTypes &&
        matchesIdPp &&
        matchesProgram &&
        matchesProject &&
        matchesFolder &&
        matchesFolderOnlyVisibility &&
        matchesIntervenant &&
        matchesOrigin &&
        matchesDocType &&
        matchesDate
      );
    });

    setDataFiltered(datasFiltered);
  }, [filter, folderFilterIds, dataDocuments, idPp, setDataFiltered]);

  return (
    <div className="relative">
      <div className="mb-8">
        {hasAccessToEdit && !hidePostActions && (
          <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
            {showAddFolderButton && (
              <AddDocumentFolderButton
                requireChildFolder={requireChildFolderForAddFolder}
                ownerId={ownerId}
              />
            )}
            <ImportDocumentFolderButton ownerId={ownerId} />
            <AddDocuments />
          </div>
        )}
        {!hideFilters && <div className="md:w-1/4">
          <div className="relative">
            <Input
              type="search"
              placeholder="Rechercher un fichier"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onBlur={(e) =>
                handleChange("search", e.target.value ? [e.target.value] : [])
              }
              className="h-11 text-[14px]"
            />
            <Search
              size={16}
              className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none"
            />
          </div>
        </div>}

        {!hideFilters && <div className="flex justify-between flex-wrap items-start mt-3">
          <div className="md:w-2/3 flex gap-x-3 gap-y-2 items-center flex-wrap md:mb-0 mb-5">
            {!hideProjectProgramCategoryFilters && (
              <DropdownFilterCat
                options={typesDocs}
                name="Categories"
                slug="categories"
                value={filter.categories}
                handleChange={handleChange}
              />
            )}

            <DropdownFilterType
              options={optionsType}
              name="Types"
              slug="types"
              value={filter.types}
              handleChange={handleChange}
            />

            <DateFilter
              handleChange={handleChange}
              initialAfter={filter.after}
              initialBefore={filter.before}
            />

            {/* PROGRAMMES */}
            {!hideProjectProgramCategoryFilters && programsList && programsList.length > 0 && (
              <div className="relative">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-xs py-[3px] h-[26px] border rounded-md px-3 font-normal flex gap-1 items-center">
                      {t("Programmes")}
                      {filter.programs.length > 0 && (
                        <span className="ml-1 text-[10px] opacity-60">
                          ({filter.programs.length})
                        </span>
                      )}
                      <ChevronDown size={16} />
                    </button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    onCloseAutoFocus={(e) => e.preventDefault()}
                    className="max-h-64 overflow-auto"
                  >
                    <DropdownMenuLabel>
                      <div className="flex gap-1 items-center">
                        <Checkbox
                          id="allprograms"
                          checked={filter.programs.length === 0}
                          onCheckedChange={() => handleChange("programs", [])}
                        />
                        <label htmlFor="allprograms">{t("Programmes")}</label>
                      </div>
                    </DropdownMenuLabel>

                    <DropdownMenuSeparator />

                    {programsList.map(
                      (option: any, i: number) =>
                        option.name && (
                          <DropdownMenuCheckboxItem
                            key={i}
                            checked={filter.programs.includes(option.id)}
                            onSelect={(e) => e.preventDefault()}
                            onCheckedChange={(checked) => {
                              let updated = [...filter.programs];

                              if (checked) {
                                updated.push(option.id);
                              } else {
                                updated = updated.filter(
                                  (id) => id !== option.id,
                                );
                              }

                              handleChange("programs", updated);
                            }}
                            className="flex items-center gap-2 pl-2"
                          >
                            <Checkbox
                              checked={filter.programs.includes(option.id)}
                              className="pointer-events-none"
                            />
                            <span>{option.name}</span>
                          </DropdownMenuCheckboxItem>
                        ),
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}

            {/* PROJETS */}
            {!hideProjectProgramCategoryFilters && projectList && projectList.length > 0 && (
              <div className="relative">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-xs py-[3px] h-[26px] border rounded-md px-3 font-normal flex gap-1 items-center">
                      {t("Projets")}
                      {filter.projects.length > 0 && (
                        <span className="ml-1 text-[10px] opacity-60">
                          ({filter.projects.length})
                        </span>
                      )}
                      <ChevronDown size={16} />
                    </button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent
                    onCloseAutoFocus={(e) => e.preventDefault()}
                    className="max-h-64 overflow-auto"
                  >
                    <DropdownMenuLabel>
                      <div className="flex gap-1 items-center">
                        <Checkbox
                          id="allprojects"
                          checked={filter.projects.length === 0}
                          onCheckedChange={() => handleChange("projects", [])}
                        />
                        <label htmlFor="allprojects">{t("Projets")}</label>
                      </div>
                    </DropdownMenuLabel>

                    <DropdownMenuSeparator />

                    {projectList
                      .filter((project: any) => {
                        if (filter.programs.length === 0) return true;
                        return validProjectIds.includes(project.id);
                      })
                      .map(
                        (option: any, i: number) =>
                          option.name && (
                            <DropdownMenuCheckboxItem
                              key={i}
                              checked={filter.projects.includes(option.id)}
                              onSelect={(e) => e.preventDefault()}
                              onCheckedChange={(checked) => {
                                let updated = [...filter.projects];

                                if (checked) {
                                  updated.push(option.id);
                                } else {
                                  updated = updated.filter(
                                    (id) => id !== option.id,
                                  );
                                }

                                handleChange("projects", updated);
                              }}
                              className="flex items-center gap-2 pl-2"
                            >
                              <Checkbox
                                checked={filter.projects.includes(option.id)}
                                className="pointer-events-none"
                              />
                              <span>{option.name}</span>
                            </DropdownMenuCheckboxItem>
                          ),
                      )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </div>}

        {!hideFilters && !hideProjectProgramCategoryFilters && showSelectedCategories && selectedCategoryLabels.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">
              {t("Catégories prises en compte")} :
            </span>
            {selectedCategoryLabels.map((label) => (
              <span
                key={label}
                className="rounded-full bg-muted px-2 py-1 text-xs text-foreground"
              >
                {label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterDocument;
